// frontend/src/pages/DashboardPage.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import {
  PlusIcon,
  ArrowUpTrayIcon,
  UserPlusIcon,
  BookOpenIcon,
  ArrowRightIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import { getDashboardSummary, getDashboard } from "../api/analytics";
import { getUsage } from "../api/orgs";
import { getAccessToken } from "../api/client";
import ErrorBanner from "../components/ErrorBanner";
import UsageWidget from "../components/UsageWidget";
import AlertsPanel from "../components/AlertsPanel";

/** Trailing window (days) for the sparkline teaser + recent KPIs. */
const SPARK_DAYS = 7;

// --------------------------------------------------------------------------- //
// Small helpers                                                                //
// --------------------------------------------------------------------------- //

/** ISO country code → flag emoji (two regional indicator symbols). */
function countryFlag(cc) {
  if (!cc || cc.length !== 2) return "🌐";
  return [...cc.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
}

/** Device type → icon character. */
function deviceIcon(dt) {
  if (!dt) return "📱";
  const t = dt.toLowerCase();
  if (t === "desktop") return "🖥️";
  if (t === "tablet") return "📟";
  if (t === "bot") return "🤖";
  return "📱";
}

/** Human-readable "X seconds / minutes ago" label. */
function timeAgo(isoStr) {
  if (!isoStr) return "";
  const delta = Math.max(0, Math.round((Date.now() - new Date(isoStr).getTime()) / 1000));
  if (delta < 60) return `${delta}s ago`;
  const mins = Math.round(delta / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.round(mins / 60)}h ago`;
}

// --------------------------------------------------------------------------- //
// Custom hook: connect to the live SSE stream                                  //
// --------------------------------------------------------------------------- //

/**
 * useLiveScans — subscribe to GET /api/dashboard/live-stream via fetch
 * (native EventSource does not support custom request headers, so we stream
 * with fetch and parse the SSE frames ourselves).
 *
 * Returns:
 *   connected {boolean}  — true while the stream is open
 *   scans     {Array}    — last 10 scan events, newest first
 *   lastScan  {string|null} — ISO timestamp of the most recent scan
 *   liveTotal {number}   — count of scans received during this session
 */
function useLiveScans(baseTotal) {
  const [connected, setConnected] = useState(false);
  const [scans, setScans] = useState([]);
  const [lastScan, setLastScan] = useState(null);
  const [liveTotal, setLiveTotal] = useState(baseTotal ?? 0);
  const abortRef = useRef(null);

  // Keep liveTotal in sync when the initial REST data arrives.
  useEffect(() => {
    if (baseTotal !== null && baseTotal !== undefined) {
      setLiveTotal((prev) => Math.max(prev, baseTotal));
    }
  }, [baseTotal]);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;

    // Relative by default (same-origin, via the dev Vite proxy / prod nginx) so
    // the request stays first-party — see api/client.js for the full rationale.
    const API = import.meta.env.VITE_API_URL || "";
    const token = getAccessToken();

    (async () => {
      try {
        const res = await fetch(`${API}/api/dashboard/live-stream`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        });
        if (!res.ok || !res.body) return;

        setConnected(true);

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });

          // Parse complete SSE lines from the buffer.
          const parts = buf.split("\n");
          buf = parts.pop(); // keep the incomplete trailing line

          for (const line of parts) {
            if (!line.startsWith("data: ")) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (!event.timestamp) continue; // skip malformed
              setScans((prev) => [event, ...prev].slice(0, 10));
              setLastScan(event.timestamp);
              setLiveTotal((prev) => prev + 1);
            } catch {
              // ignore malformed JSON frames
            }
          }
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          // Network / parse error — connection silently dropped.
        }
      } finally {
        setConnected(false);
      }
    })();

    return () => controller.abort();
  }, []); // connect once on mount

  return { connected, scans, lastScan, liveTotal };
}

// --------------------------------------------------------------------------- //
// Sub-components                                                               //
// --------------------------------------------------------------------------- //

function StatCard({ label, value }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-1">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        {label}
      </span>
      <span className="text-3xl font-bold text-gray-900">
        {value.toLocaleString()}
      </span>
    </div>
  );
}
StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
};

/** Pulsing "LIVE" badge shown while the SSE connection is active. */
function LiveBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-xs font-semibold text-red-600">
      <span
        aria-hidden="true"
        className="h-2 w-2 rounded-full bg-red-500 animate-pulse"
      />
      LIVE
    </span>
  );
}

/** Single row in the live scan feed. */
function ScanRow({ scan }) {
  const [, forceUpdate] = useState(0);
  // Re-render every 15s so the "X ago" label stays fresh without a global timer.
  useEffect(() => {
    const id = setInterval(() => forceUpdate((n) => n + 1), 15_000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-gray-100 last:border-0 text-sm">
      <span className="text-base" title={scan.country_code}>
        {countryFlag(scan.country_code)}
      </span>
      <span className="text-base" title={scan.device_type}>
        {deviceIcon(scan.device_type)}
      </span>
      <span className="font-mono text-gray-800 truncate flex-1">
        {scan.code_name ?? "—"}
      </span>
      <span className="text-gray-400 text-xs whitespace-nowrap">
        {timeAgo(scan.timestamp)}
      </span>
    </div>
  );
}
ScanRow.propTypes = {
  scan: PropTypes.shape({
    code_name: PropTypes.string,
    country_code: PropTypes.string,
    device_type: PropTypes.string,
    timestamp: PropTypes.string,
  }).isRequired,
};

/** Scrolling live scan feed panel. */
function LiveFeed({ connected, scans, lastScan }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-700">Live Scans</h2>
        {connected ? (
          <LiveBadge />
        ) : (
          <span className="text-xs text-gray-400">connecting…</span>
        )}
      </div>

      {lastScan && (
        <p className="text-xs text-gray-400 mb-2">
          Last scan: {timeAgo(lastScan)}
        </p>
      )}

      {scans.length === 0 ? (
        <p className="text-sm text-gray-400 italic py-4 text-center">
          Waiting for scans…
        </p>
      ) : (
        <div className="overflow-y-auto max-h-56">
          {scans.map((scan, i) => (
            <ScanRow key={`${scan.timestamp}-${i}`} scan={scan} />
          ))}
        </div>
      )}
    </div>
  );
}
LiveFeed.propTypes = {
  connected: PropTypes.bool.isRequired,
  scans: PropTypes.array.isRequired,
  lastScan: PropTypes.string,
};

/** Skeleton placeholder shown while the summary loads. */
function DashboardSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading dashboard"
      className="max-w-6xl mx-auto space-y-6 animate-pulse"
    >
      <div className="h-6 w-40 bg-gray-200 rounded" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-24 bg-gray-100 rounded-xl border border-gray-200"
          />
        ))}
      </div>
      <div className="h-64 bg-gray-100 rounded-xl border border-gray-200" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="h-64 bg-gray-100 rounded-xl border border-gray-200" />
        <div className="h-64 bg-gray-100 rounded-xl border border-gray-200" />
      </div>
    </div>
  );
}

/** Quick-actions row — the dashboard's "act now" shortcuts (PA2-T4). */
function QuickActions() {
  const actions = [
    { label: "New code", to: "/codes", icon: PlusIcon },
    { label: "Import CSV", to: "/batch", icon: ArrowUpTrayIcon },
    { label: "Invite teammate", to: "/settings/org", icon: UserPlusIcon },
    { label: "View docs", href: "/docs", icon: BookOpenIcon },
  ];
  const cls =
    "flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700";
  return (
    <div className="flex flex-wrap gap-3">
      {actions.map(({ label, to, href, icon: ActIcon }) =>
        href ? (
          <a key={label} href={href} target="_blank" rel="noreferrer" className={cls}>
            <ActIcon className="h-4 w-4" />
            {label}
          </a>
        ) : (
          <Link key={label} to={to} className={cls}>
            <ActIcon className="h-4 w-4" />
            {label}
          </Link>
        ),
      )}
    </div>
  );
}

/** Copy a code's public short link to the clipboard. */
function copyShortLink(code) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = `${origin}/${code}`;
  if (navigator?.clipboard) {
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("Link copied"))
      .catch(() => toast.error("Copy failed — copy manually"));
  }
}

/** Top codes by lifetime scans, with inline quick actions (PA2-T3). */
function TopCodesPanel({ codes }) {
  const top = (codes || []).slice(0, 5);
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Top codes</h2>
        <Link to="/codes" className="text-xs font-medium text-brand-700 hover:text-brand-800">
          All codes →
        </Link>
      </div>
      {top.length === 0 ? (
        <p className="py-4 text-center text-sm italic text-gray-400">No codes yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {top.map((c) => (
            <li key={c.code} className="flex items-center gap-2 py-2 text-sm">
              <Link
                to={`/codes/${c.code}`}
                className="flex-1 truncate font-mono text-gray-800 hover:text-brand-700"
              >
                {c.code}
              </Link>
              <span className="tabular-nums text-gray-500">
                {c.scan_count.toLocaleString()} scans
              </span>
              <button
                type="button"
                onClick={() => copyShortLink(c.code)}
                title="Copy public link"
                className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
              >
                <LinkIcon className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
TopCodesPanel.propTypes = {
  codes: PropTypes.arrayOf(
    PropTypes.shape({ code: PropTypes.string, scan_count: PropTypes.number }),
  ),
};
TopCodesPanel.defaultProps = { codes: [] };

/** Short "5 Jun"-style day label from a YYYY-MM-DD string (local, no TZ shift). */
function fmtDay(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Dependency-free filled area sparkline (PA2-T6) — fills its container height so
 * the trend card reads as a real chart, not a thin floating line. The line stroke
 * stays even at any width via non-scaling-stroke.
 */
function Sparkline({ data }) {
  const pts = (data || []).map((d) => d.count ?? 0);
  if (pts.length < 2) return null;
  const max = Math.max(...pts, 1);
  const W = 240;
  const H = 64;
  const step = W / (pts.length - 1);
  const xy = pts.map((v, i) => [i * step, H - (v / max) * H]);
  const line = xy
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`)
    .join(" ");
  const area = `${line} L${W},${H} L0,${H} Z`;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="h-28 w-full text-brand-500"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.22" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#spark-fill)" stroke="none" />
      <path
        d={line}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
Sparkline.propTypes = { data: PropTypes.array };
Sparkline.defaultProps = { data: [] };

/** 7-day scan trend teaser linking through to the Analytics deep-dive. */
function TrendTeaser({ data }) {
  const series = data || [];
  const counts = series.map((d) => d.count ?? 0);
  const total = counts.reduce((a, b) => a + b, 0);
  const peak = counts.length ? Math.max(...counts) : 0;
  const hasData = counts.length >= 2 && total > 0;

  return (
    <Link
      to="/analytics"
      className="group flex flex-col rounded-xl border border-gray-200 bg-white p-4 transition-colors hover:border-brand-300"
    >
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Last 7 days</h2>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-700">
          Explore analytics
          <ArrowRightIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>

      {hasData ? (
        <>
          <div className="mb-3 flex items-baseline gap-2">
            <span className="text-2xl font-semibold text-gray-900">
              {total.toLocaleString()}
            </span>
            <span className="text-xs text-gray-500">
              scans · peak {peak.toLocaleString()}/day
            </span>
          </div>
          <Sparkline data={series} />
          <div className="mt-1 flex items-center justify-between text-[11px] text-gray-400">
            <span>{fmtDay(series[0]?.date)}</span>
            <span>Today</span>
          </div>
        </>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center py-10 text-center">
          <p className="text-sm text-gray-500">No scans in the last 7 days yet.</p>
          <p className="mt-1 text-xs text-gray-400">
            Share a code — scans will trend here.
          </p>
        </div>
      )}
    </Link>
  );
}
TrendTeaser.propTypes = { data: PropTypes.array };
TrendTeaser.defaultProps = { data: [] };

/** First-run guidance for an org with no codes yet (PA2-T7). */
function OnboardingEmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-brand-300 bg-brand-50/50 p-8 text-center">
      <h2 className="text-lg font-semibold text-gray-900">Create your first QR code</h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-gray-600">
        Generate a dynamic QR code, share it anywhere, and watch scans roll in here in
        real time.
      </p>
      <div className="mt-4 flex justify-center gap-3">
        <Link
          to="/codes"
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
        >
          <PlusIcon className="h-4 w-4" />
          Create a code
        </Link>
        <a
          href="/docs"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
        >
          <BookOpenIcon className="h-4 w-4" />
          Read the guide
        </a>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Page                                                                         //
// --------------------------------------------------------------------------- //

/**
 * DashboardPage — the real-time **command center** (now & act): headline KPIs,
 * a live SSE scan feed, plan usage + alerts, top codes with quick actions, and a
 * 7-day trend teaser that hands off to the Analytics deep-dive. Historical
 * day/device/country charts live in Analytics, not here (PRIVATE-AREA-PLAN PA2).
 *
 * Driven by ``GET /api/dashboard/summary`` (KPIs + 7-day series),
 * ``GET /api/dashboard`` (top codes + total count), ``GET /api/orgs/me/usage``
 * (usage/alerts), and ``GET /api/dashboard/live-stream`` (SSE, mounted once).
 */
function DashboardPage() {
  const [summary, setSummary] = useState(null);
  const [stats, setStats] = useState(null); // legacy /dashboard: top codes + counts
  const [usage, setUsage] = useState(null); // shared by UsageWidget + AlertsPanel
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // `silent` re-fetches without flipping the loading skeleton, so a background
  // refresh (after a live scan) updates panels in place instead of blanking them.
  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    // Summary is the primary payload (gates loading/error); stats + usage are
    // best-effort enrichments that never block or break the page.
    getDashboard()
      .then(setStats)
      .catch(() => {});
    getUsage()
      .then(setUsage)
      .catch(() => {});
    return getDashboardSummary(SPARK_DAYS)
      .then(setSummary)
      .catch(() => {
        if (!silent) setError("Failed to load dashboard.");
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Base total comes from the REST summary; the live hook ticks it up.
  const baseTotal = summary?.summary?.total_scans ?? null;
  const { connected, scans, lastScan, liveTotal } = useLiveScans(baseTotal);

  // A live scan changes the underlying aggregates; silently reload after a short
  // debounce (long enough for the matview refresh + cache invalidation to land,
  // and coalescing a burst of scans into a single reload).
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);
  useEffect(() => {
    if (!lastScan) return undefined;
    const id = setTimeout(() => loadRef.current(true), 4000);
    return () => clearTimeout(id);
  }, [lastScan]);

  if (loading) {
    return <DashboardSkeleton />;
  }
  if (error || !summary) {
    return (
      <div className="max-w-6xl mx-auto py-8 space-y-4">
        <ErrorBanner
          message={error ?? "Unable to load dashboard."}
          onRetry={load}
        />
      </div>
    );
  }

  const kpi = summary.summary;
  // First-run when the org has no codes at all (stats is best-effort, so only
  // treat a confirmed zero as empty — never the not-yet-loaded null).
  const isFirstRun = stats?.total_codes === 0;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header + live status */}
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900">Dashboard</h1>
        {connected && <LiveBadge />}
        {lastScan && (
          <span className="text-xs text-gray-400 hidden sm:block">
            Last scan: {timeAgo(lastScan)}
          </span>
        )}
      </div>

      {/* Act-now shortcuts */}
      <QuickActions />

      {isFirstRun ? (
        <OnboardingEmptyState />
      ) : (
        <>
          {/* KPI cards — "Total Scans" ticks up in real time via liveTotal */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Scans" value={liveTotal} />
            <StatCard label="Scans (Last 7 Days)" value={kpi.scans_last_7_days} />
            <StatCard label="Active Codes" value={kpi.active_codes_count} />
            <StatCard label="Unique Visitors" value={kpi.unique_ips_last_30_days} />
          </div>

          {/* Real-time centrepiece: live feed beside plan usage + alerts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <LiveFeed connected={connected} scans={scans} lastScan={lastScan} />
            <div className="space-y-6">
              <UsageWidget usage={usage} />
              <AlertsPanel usage={usage} />
            </div>
          </div>

          {/* Top codes + 7-day trend teaser → Analytics deep-dive */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TopCodesPanel codes={stats?.top_codes} />
            <TrendTeaser data={summary.scans_by_day} />
          </div>
        </>
      )}
    </div>
  );
}

export default DashboardPage;
